import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, MessageSquare } from 'lucide-react';
import { navLinks } from '../../data/mock';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const observerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sectionIds = navLinks.map((l) => l.href.replace('#', ''));
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current && observerRef.current.disconnect();
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const handleOpenChat = () => {
    setMobileOpen(false);
    window.dispatchEvent(new CustomEvent('openChat'));
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'navbar-glass py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-6 lg:px-16 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="font-display font-bold text-xl text-[#F0F4FF] hover:text-[#00D4FF] transition-colors duration-300"
        >
          NK<span className="text-[#00D4FF]">.</span>
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link.href)}
              className={`font-mono text-sm transition-colors duration-300 ${
                activeSection === link.href.replace('#', '')
                  ? 'text-[#00D4FF]'
                  : 'text-[#8892A4] hover:text-[#00D4FF]'
              }`}
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={handleOpenChat}
            className="px-4 py-2 rounded-lg border border-[#00D4FF] text-[#00D4FF] font-mono text-sm hover:bg-[#00D4FF] hover:text-[#080C14] transition-all duration-300 flex items-center gap-2 glow-cyan-subtle"
          >
            <MessageSquare size={14} />
            Chat with AI
          </button>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          className="md:hidden text-[#F0F4FF] p-1"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden navbar-glass mt-2 mx-4 rounded-xl p-4">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNavClick(link.href)}
              className="block w-full text-left py-3 font-mono text-sm text-[#8892A4] hover:text-[#00D4FF] transition-colors duration-300"
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={handleOpenChat}
            className="mt-2 w-full px-4 py-2.5 rounded-lg border border-[#00D4FF] text-[#00D4FF] font-mono text-sm flex items-center justify-center gap-2"
          >
            <MessageSquare size={14} />
            Chat with AI
          </button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
